import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { formatCurrency, formatNumber } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
    Download,
    Loader2,
    RotateCcw,
    Play,
    AlertCircle,
    FileText,
    ChevronUp,
    ChevronDown
} from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import FinanceSnapshot from '../../components/FinanceSnapshot';

// Helper to get date presets
const getDatePresets = () => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = now.getMonth();
    
    // Last month
    const lastMonth = new Date(currentYear, currentMonth - 1, 1);
    const lastMonthEnd = new Date(currentYear, currentMonth, 0);
    
    // QTD (Quarter to date)
    const currentQuarter = Math.floor(currentMonth / 3);
    const qtdStart = new Date(currentYear, currentQuarter * 3, 1);
    
    // YTD (Year to date)
    const ytdStart = new Date(currentYear, 0, 1);
    
    // Last 3, 6, 12 months
    const last3Start = new Date(currentYear, currentMonth - 2, 1);
    const last6Start = new Date(currentYear, currentMonth - 5, 1);
    const last12Start = new Date(currentYear, currentMonth - 11, 1);
    
    const formatYYYYMM = (date) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        return `${y}-${m}`;
    };
    
    return {
        last_month: { start: formatYYYYMM(lastMonth), end: formatYYYYMM(lastMonthEnd), label: 'Last Month' },
        last_3:     { start: formatYYYYMM(last3Start), end: formatYYYYMM(now), label: 'Last 3 Months' },
        last_6:     { start: formatYYYYMM(last6Start), end: formatYYYYMM(now), label: 'Last 6 Months' },
        last_12:    { start: formatYYYYMM(last12Start), end: formatYYYYMM(now), label: 'Last 12 Months' },
        qtd:        { start: formatYYYYMM(qtdStart), end: formatYYYYMM(now), label: 'Quarter to Date' },
        ytd:        { start: formatYYYYMM(ytdStart), end: formatYYYYMM(now), label: 'Year to Date' },
    };
};

const InternalReportsPage = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const isFinance = user?.role === 'finance';
    
    // Filter state - initialize from URL params
    const [filters, setFilters] = useState(() => {
        const presets = getDatePresets();
        return {
            start_date: searchParams.get('start') || presets.last_6.start,
            end_date: searchParams.get('end') || presets.last_6.end,
            group_by: searchParams.get('groupBy') || 'month',
            revenue_type: searchParams.get('type') || 'gross',
            label_id: searchParams.get('label') || '',
            month_type: searchParams.get('view') || 'transaction_month'  // 'transaction_month' = Performance, 'reporting_month' = Cash Flow
        };
    });
    const [periodPreset, setPeriodPreset] = useState(searchParams.get('period') || 'last_6');
    
    // Data state
    const [loading, setLoading] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState(null);
    const [reportData, setReportData] = useState(null);
    const [labels, setLabels] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });
    
    // Pagination
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 25;
    
    // Fetch labels for filter dropdown
    useEffect(() => {
        const fetchLabels = async () => {
            try {
                const response = await api.get('/labels');
                setLabels(response.data || []);
            } catch (err) {
                console.error('Failed to fetch labels:', err);
            }
        };
        fetchLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    // Sync filters to URL
    useEffect(() => {
        const params = new URLSearchParams();
        if (filters.start_date) params.set('start', filters.start_date);
        if (filters.end_date) params.set('end', filters.end_date);
        if (filters.group_by) params.set('groupBy', filters.group_by);
        if (filters.revenue_type) params.set('type', filters.revenue_type);
        if (filters.label_id) params.set('label', filters.label_id);
        if (filters.month_type) params.set('view', filters.month_type);
        if (periodPreset) params.set('period', periodPreset);
        setSearchParams(params, { replace: true });
    }, [filters, periodPreset, setSearchParams]);
    
    // Run report
    const runReport = useCallback(async () => {
        setLoading(true);
        setError(null);
        setCurrentPage(1);
        
        try {
            const params = new URLSearchParams();
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);
            if (filters.group_by) params.append('group_by', filters.group_by);
            if (filters.label_id) params.append('label_id', filters.label_id);
            if (filters.month_type) params.append('month_type', filters.month_type);
            
            const response = await api.get(`/revenue/report?${params.toString()}`);
            setReportData(response.data);
        } catch (err) {
            console.error('Failed to fetch report:', err);
            setError(err.response?.data?.detail || 'Failed to load report data');
            setReportData(null);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    
    // When user flips Performance↔Cash Flow, the report is re-fetched via runReport (no auto date change)
    
    // Reset filters — use Last 6 Months default
    const resetFilters = () => {
        const presets = getDatePresets();
        setPeriodPreset('last_6');
        setFilters(prev => ({
            ...prev,
            start_date: presets.last_6.start,
            end_date: presets.last_6.end,
            group_by: 'month',
            revenue_type: 'gross',
            label_id: ''
        }));
        setSortConfig({ key: null, direction: 'desc' });
    };
    
    // Apply preset
    const applyPreset = (preset) => {
        const presets = getDatePresets();
        if (preset === 'all_time') {
            setPeriodPreset('all_time');
            setFilters(prev => ({ ...prev, start_date: '', end_date: '' }));
            return;
        }
        if (preset === 'custom') {
            setPeriodPreset('custom');
            return;
        }
        if (presets[preset]) {
            setPeriodPreset(preset);
            setFilters(prev => ({
                ...prev,
                start_date: presets[preset].start,
                end_date: presets[preset].end
            }));
        }
    };
    
    // Export
    const handleExport = async () => {
        setExporting(true);
        try {
            const params = new URLSearchParams();
            if (filters.start_date) params.append('start_date', filters.start_date);
            if (filters.end_date) params.append('end_date', filters.end_date);
            if (filters.label_id) params.append('label_id', filters.label_id);
            if (filters.month_type) params.append('month_type', filters.month_type);
            
            const response = await api.get(`/revenue/export?${params.toString()}`, {
                responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            const view = filters.month_type === 'reporting_month' ? 'cashflow' : 'performance';
            const contentType = response.headers?.['content-type'] || '';
            const extension = contentType.includes('text/csv') ? 'csv' : 'xlsx';
            link.setAttribute('download', `revenue_${view}_${filters.start_date}_${filters.end_date}.${extension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export failed:', err);

            let message = 'Export failed. Please try again.';

            try {
                if (err.response?.data instanceof Blob) {
                    const text = await err.response.data.text();
                    const payload = JSON.parse(text);

                    if (typeof payload?.detail === 'string') {
                        message = payload.detail;
                    } else if (payload?.detail?.message) {
                        message = payload.detail.message;
                    }
                } else if (err.response?.data?.detail?.message) {
                    message = err.response.data.detail.message;
                } else if (typeof err.response?.data?.detail === 'string') {
                    message = err.response.data.detail;
                }
            } catch (parseError) {
                console.error('Failed to parse export error:', parseError);
            }

            setError(message);
        } finally {
            setExporting(false);
        }
    };
    
    // Sort handler
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === 'desc' ? 'asc' : 'desc'
        }));
    };
    
    // Sorted and paginated data
    const processedData = useMemo(() => {
        if (!reportData?.data) return [];
        
        let data = [...reportData.data];
        
        // Sort
        if (sortConfig.key) {
            data.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                
                // Handle numeric values
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
                }
                
                // Handle strings
                aVal = String(aVal || '').toLowerCase();
                bVal = String(bVal || '').toLowerCase();
                if (sortConfig.direction === 'asc') {
                    return aVal.localeCompare(bVal);
                }
                return bVal.localeCompare(aVal);
            });
        }
        
        return data;
    }, [reportData, sortConfig]);
    
    // Paginated data
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * pageSize;
        return processedData.slice(start, start + pageSize);
    }, [processedData, currentPage]);
    
    const totalPages = Math.ceil(processedData.length / pageSize);
    
    // Column config based on group_by
    const columns = useMemo(() => {
        const revenueType = filters.revenue_type;
        const baseColumns = [];
        
        switch (filters.group_by) {
            case 'month':
                baseColumns.push({ key: 'group', label: filters.month_type === 'reporting_month' ? 'Reporting Month' : 'Transaction Month', align: 'left' });
                break;
            case 'label':
                baseColumns.push({ key: 'label_name', label: 'Label', align: 'left' });
                break;
            case 'platform':
                baseColumns.push({ key: 'group', label: 'Platform', align: 'left' });
                break;
            case 'country':
                baseColumns.push({ key: 'group', label: 'Country', align: 'left' });
                break;
            case 'artist':
                baseColumns.push({ key: 'group', label: 'Artist', align: 'left' });
                break;
            default:
                baseColumns.push({ key: 'group', label: 'Group', align: 'left' });
        }
        
        if (revenueType === 'gross') {
            baseColumns.push(
                { key: 'gross_inr', label: 'Gross (INR)', align: 'right', format: 'currency_inr' },
                { key: 'gross_usd', label: 'Gross (USD)', align: 'right', format: 'currency' }
            );
        } else {
            baseColumns.push(
                { key: 'net_inr', label: 'Net (INR)', align: 'right', format: 'currency_inr' },
                { key: 'net_usd', label: 'Net (USD)', align: 'right', format: 'currency' }
            );
        }
        
        baseColumns.push(
            { key: 'units', label: 'Units', align: 'right', format: 'number' },
            { key: 'entries', label: 'Transactions', align: 'right', format: 'number' }
        );
        
        return baseColumns;
    }, [filters.group_by, filters.revenue_type, filters.month_type]);
    
    // Format cell value
    const formatValue = (value, format) => {
        if (value === null || value === undefined) return '-';
        switch (format) {
            case 'currency':
                return formatCurrency(value);
            case 'currency_inr':
                return formatCurrency(value, 'INR');
            case 'number':
                return formatNumber(value);
            default:
                return value;
        }
    };
    
    // Sort icon
    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) return null;
        return sortConfig.direction === 'asc' ? 
            <ChevronUp className="w-4 h-4 inline ml-1" /> : 
            <ChevronDown className="w-4 h-4 inline ml-1" />;
    };
    
    return (
        <div className="space-y-6" data-testid="internal-reports-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-heading font-bold tracking-tight">Internal Reports</h1>
                    <p className="text-muted-foreground">
                        {filters.month_type === 'reporting_month' 
                            ? 'Cash Flow view — grouped by reporting month (when platforms paid us)'
                            : 'Performance view — grouped by transaction month (when streams happened)'}
                    </p>
                </div>
                {/* View Mode Toggle */}
                <div className="flex items-center gap-1 p-1 bg-muted/30 rounded-lg" data-testid="reports-view-toggle">
                    <button
                        type="button"
                        onClick={() => setFilters(prev => ({ ...prev, month_type: 'transaction_month' }))}
                        data-testid="reports-toggle-performance"
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                            filters.month_type === 'transaction_month'
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Performance
                    </button>
                    <button
                        type="button"
                        onClick={() => setFilters(prev => ({ ...prev, month_type: 'reporting_month' }))}
                        data-testid="reports-toggle-cashflow"
                        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                            filters.month_type === 'reporting_month'
                                ? 'bg-primary text-primary-foreground shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                        }`}
                    >
                        Cash Flow
                    </button>
                </div>
            </div>

            {/* Finance Snapshot — only for Finance role */}
            {isFinance && <FinanceSnapshot />}
            
            {/* Sticky Filter Bar */}
            <Card className="bg-card/50 border-border/40 sticky top-16 z-20 backdrop-blur-sm">
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-end gap-4">
                        {/* Period Selector — simple single dropdown */}
                        <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Period</Label>
                            <Select value={periodPreset} onValueChange={applyPreset}>
                                <SelectTrigger className="w-44 h-9 bg-background/50" data-testid="filter-period">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="last_month">Last Month</SelectItem>
                                    <SelectItem value="last_3">Last 3 Months</SelectItem>
                                    <SelectItem value="last_6">Last 6 Months</SelectItem>
                                    <SelectItem value="last_12">Last 12 Months</SelectItem>
                                    <SelectItem value="qtd">Quarter to Date</SelectItem>
                                    <SelectItem value="ytd">Year to Date</SelectItem>
                                    <SelectItem value="all_time">All Time</SelectItem>
                                    <SelectItem value="custom">Custom Range</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Custom Date Range — only visible when 'custom' is selected */}
                        {periodPreset === 'custom' && (
                            <div className="flex gap-2 items-end">
                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">From (Month)</Label>
                                    <Input
                                        type="month"
                                        value={filters.start_date}
                                        onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                                        className="w-40 h-9 bg-background/50"
                                        data-testid="filter-start-date"
                                    />
                                </div>
                                <div className="space-y-1">
                                    <Label className="text-xs text-muted-foreground">To (Month)</Label>
                                    <Input
                                        type="month"
                                        value={filters.end_date}
                                        onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                                        className="w-40 h-9 bg-background/50"
                                        data-testid="filter-end-date"
                                    />
                                </div>
                            </div>
                        )}
                        
                        {/* Group By */}
                        <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Group By</Label>
                            <Select 
                                value={filters.group_by} 
                                onValueChange={(val) => setFilters(prev => ({ ...prev, group_by: val }))}
                            >
                                <SelectTrigger className="w-32 h-9 bg-background/50" data-testid="filter-group-by">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="month">Month</SelectItem>
                                    <SelectItem value="label">Label</SelectItem>
                                    <SelectItem value="platform">Platform</SelectItem>
                                    <SelectItem value="country">Country</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Revenue Type */}
                        <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Revenue</Label>
                            <Select 
                                value={filters.revenue_type} 
                                onValueChange={(val) => setFilters(prev => ({ ...prev, revenue_type: val }))}
                            >
                                <SelectTrigger className="w-24 h-9 bg-background/50" data-testid="filter-revenue-type">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="gross">Gross</SelectItem>
                                    <SelectItem value="net">Net</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Label Filter */}
                        <div className="space-y-1">
                            <Label className="text-xs text-muted-foreground">Label</Label>
                            <Select 
                                value={filters.label_id || 'all'} 
                                onValueChange={(val) => setFilters(prev => ({ ...prev, label_id: val === 'all' ? '' : val }))}
                            >
                                <SelectTrigger className="w-40 h-9 bg-background/50" data-testid="filter-label">
                                    <SelectValue placeholder="All Labels" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Labels</SelectItem>
                                    {labels.map(label => (
                                        <SelectItem key={label.id} value={label.id}>
                                            {label.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        
                        {/* Actions */}
                        <div className="flex gap-2 ml-auto">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={resetFilters}
                                className="h-9"
                                data-testid="reset-filters-btn"
                            >
                                <RotateCcw className="w-4 h-4 mr-1" />
                                Reset
                            </Button>
                            <Button 
                                onClick={runReport} 
                                disabled={loading}
                                className="h-9 bg-primary hover:bg-primary/90"
                                data-testid="run-report-btn"
                            >
                                {loading ? (
                                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                                ) : (
                                    <Play className="w-4 h-4 mr-1" />
                                )}
                                Run Report
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>
            
            {/* Summary Cards */}
            {reportData?.summary && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="bg-card/50 border-border/40">
                        <CardContent className="p-4">
                            <p className="text-xs text-muted-foreground">Total Gross (INR)</p>
                            <p className="text-xl font-heading font-bold text-secondary">
                                {formatCurrency(reportData.summary.total_gross_inr || 0, 'INR')}
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="bg-card/50 border-border/40">
                        <CardContent className="p-4">
                            <p className="text-xs text-muted-foreground">Total Net (INR)</p>
                            <p className="text-xl font-heading font-bold">
                                {formatCurrency(reportData.summary.total_net_inr || 0, 'INR')}
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="bg-card/50 border-border/40">
                        <CardContent className="p-4">
                            <p className="text-xs text-muted-foreground">Total Units</p>
                            <p className="text-xl font-heading font-bold">
                                {formatNumber(reportData.summary.total_units || 0)}
                            </p>
                        </CardContent>
                    </Card>
                    <Card className="bg-card/50 border-border/40">
                        <CardContent className="p-4">
                            <p className="text-xs text-muted-foreground">Date Range</p>
                            <p className="text-sm font-medium">
                                {reportData.date_range?.start || 'N/A'} → {reportData.date_range?.end || 'N/A'}
                            </p>
                        </CardContent>
                    </Card>
                </div>
            )}
            
            {/* Results Section */}
            <Card className="bg-card/50 border-border/40">
                <CardHeader className="flex flex-row items-center justify-between">
                    <div>
                        <CardTitle className="font-heading">
                            {reportData ? `Report Results (${processedData.length} rows)` : 'Report Results'}
                        </CardTitle>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                        <Button 
                            variant="outline" 
                            onClick={handleExport}
                            disabled={exporting || !reportData}
                            className="border-border/50"
                            data-testid="export-btn"
                        >
                            {exporting ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Download className="w-4 h-4 mr-2" />
                            )}
                            Download Detailed Data
                        </Button>
                        <p className="max-w-sm text-right text-xs text-muted-foreground">
                            Up to 3 months per download. Large datasets are automatically provided as CSV.
                        </p>
                    </div>
                </CardHeader>
                <CardContent>
                    {/* Loading State */}
                    {loading && (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-8 h-8 animate-spin text-primary" />
                            <span className="ml-3 text-muted-foreground">Loading report...</span>
                        </div>
                    )}
                    
                    {/* Error State */}
                    {error && !loading && (
                        <div className="flex items-center justify-center py-12 text-destructive">
                            <AlertCircle className="w-6 h-6 mr-2" />
                            <span>{error}</span>
                        </div>
                    )}
                    
                    {/* Empty State */}
                    {!loading && !error && reportData && processedData.length === 0 && (
                        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                            <FileText className="w-12 h-12 mb-4 opacity-50" />
                            <p className="text-lg font-medium">No revenue found for selected range</p>
                            <p className="text-sm">Try adjusting your filters or date range</p>
                        </div>
                    )}
                    
                    {/* Initial State */}
                    {!loading && !error && !reportData && (
                        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                            <FileText className="w-12 h-12 mb-4 opacity-50" />
                            <p className="text-lg font-medium">Configure filters and run report</p>
                            <p className="text-sm">Select date range, grouping, and click "Run Report"</p>
                        </div>
                    )}
                    
                    {/* Results Table */}
                    {!loading && !error && reportData && processedData.length > 0 && (
                        <>
                            <div className="rounded-lg border border-border/40 overflow-hidden">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="bg-muted/20">
                                            {columns.map((col) => (
                                                <TableHead 
                                                    key={col.key}
                                                    className={`cursor-pointer hover:bg-muted/30 ${col.align === 'right' ? 'text-right' : ''}`}
                                                    onClick={() => handleSort(col.key)}
                                                >
                                                    {col.label}
                                                    <SortIcon columnKey={col.key} />
                                                </TableHead>
                                            ))}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {paginatedData.map((row, idx) => (
                                            <TableRow key={idx} className="hover:bg-muted/10 border-border/40">
                                                {columns.map((col) => (
                                                    <TableCell 
                                                        key={col.key}
                                                        className={`${col.align === 'right' ? 'text-right font-mono' : ''} ${col.format === 'currency' ? 'text-secondary' : ''}`}
                                                    >
                                                        {formatValue(
                                                            col.key === 'label_name' ? (row.label_name || row.group) : row[col.key],
                                                            col.format
                                                        )}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                            
                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between mt-4">
                                    <p className="text-sm text-muted-foreground">
                                        Showing {((currentPage - 1) * pageSize) + 1} - {Math.min(currentPage * pageSize, processedData.length)} of {processedData.length}
                                    </p>
                                    <div className="flex gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                        >
                                            Previous
                                        </Button>
                                        <span className="flex items-center px-3 text-sm">
                                            Page {currentPage} of {totalPages}
                                        </span>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default InternalReportsPage;
